import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { CommaListField, SectionLabel, StringListEditor } from './primitives'
import { gapNumbers } from '../contentTypes'
import type { DropdownGap, DropdownGapsContent, GapsContent } from '../contentTypes'

/**
 * Редакторы типов с пропусками: перетаскивание из банка, вписывание и выбор из
 * выпадающего списка.
 *
 * Пропуски размечаются в тексте плейсхолдерами {{1}}, {{2}} — сервер текст не
 * разбирает, ключ пропуска и есть номер плейсхолдера.
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
}

function GapText({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      size="small"
      label={t('exercises.editor.gapsText')}
      helperText={t('exercises.editor.gapsTextHint')}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/** Список эталонов по каждому пропуску, найденному в тексте */
function GapAnswers({
  text,
  gaps,
  onChange,
  disabled,
}: {
  text: string
  gaps: Record<string, string[] | string>
  onChange: (gaps: Record<string, string[]>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const numbers = gapNumbers(text)

  if (numbers.length === 0) {
    return <Alert severity="info">{t('exercises.editor.gapsEmpty')}</Alert>
  }

  const answersOf = (key: string): string[] => {
    const value = gaps[key]
    if (Array.isArray(value)) return value
    return typeof value === 'string' ? [value] : ['']
  }

  const setAnswers = (key: string, values: string[]) => {
    const next: Record<string, string[]> = {}
    numbers.forEach((number) => {
      next[String(number)] = String(number) === key ? values : answersOf(String(number))
    })
    onChange(next)
  }

  return (
    <Stack spacing={2}>
      {numbers.map((number) => (
        <Box key={number}>
          <CommaListField
            label={t('exercises.editor.gapAnswers', { number })}
            helperText={t('exercises.editor.gapAnswersHint')}
            values={answersOf(String(number))}
            onChange={(values) => setAnswers(String(number), values)}
            disabled={disabled}
          />
        </Box>
      ))}
    </Stack>
  )
}

export function DragGapsEditor({ content, onChange, disabled }: EditorProps<GapsContent>) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <GapText value={content.text} onChange={(text) => onChange({ ...content, text })} disabled={disabled} />
      <GapAnswers
        text={content.text}
        gaps={content.gaps}
        onChange={(gaps) => onChange({ ...content, gaps })}
        disabled={disabled}
      />
      <CommaListField
        label={t('exercises.editor.bank')}
        helperText={t('exercises.editor.bankHint')}
        values={content.bank ?? []}
        onChange={(bank) => onChange({ ...content, bank })}
        disabled={disabled}
      />
    </Stack>
  )
}

export function ClozeEditor({ content, onChange, disabled }: EditorProps<GapsContent>) {
  return (
    <Stack spacing={2}>
      <GapText value={content.text} onChange={(text) => onChange({ ...content, text })} disabled={disabled} />
      <GapAnswers
        text={content.text}
        gaps={content.gaps}
        onChange={(gaps) => onChange({ ...content, gaps })}
        disabled={disabled}
      />
    </Stack>
  )
}

export function DropdownGapsEditor({ content, onChange, disabled }: EditorProps<DropdownGapsContent>) {
  const { t } = useTranslation()
  const numbers = gapNumbers(content.text)

  const gapAt = (index: number): DropdownGap => content.gaps[index] ?? { options: ['', ''], correct: 0 }

  const updateGap = (index: number, gap: DropdownGap) => {
    const gaps = numbers.map((_, position) => (position === index ? gap : gapAt(position)))
    onChange({ ...content, gaps })
  }

  return (
    <Stack spacing={2}>
      <GapText value={content.text} onChange={(text) => onChange({ ...content, text })} disabled={disabled} />

      {numbers.length === 0 && <Alert severity="info">{t('exercises.editor.gapsEmpty')}</Alert>}

      {numbers.map((number, index) => {
        const gap = gapAt(index)

        return (
          <Box key={number} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <SectionLabel text={t('exercises.editor.gapOptions', { number })} />
            <Stack spacing={1}>
              {gap.options.map((option, optionIndex) => (
                <Stack key={optionIndex} direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    value={option}
                    disabled={disabled}
                    onChange={(event) => {
                      const options = [...gap.options]
                      options[optionIndex] = event.target.value
                      updateGap(index, { ...gap, options })
                    }}
                  />
                  <IconButton
                    size="small"
                    disabled={disabled || gap.options.length <= 2}
                    onClick={() =>
                      updateGap(index, {
                        options: gap.options.filter((_, i) => i !== optionIndex),
                        // Верный вариант мог оказаться за пределами списка
                        correct: Math.min(gap.correct, gap.options.length - 2),
                      })
                    }
                    aria-label={t('common.delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={disabled}
                  onClick={() => updateGap(index, { ...gap, options: [...gap.options, ''] })}
                >
                  {t('exercises.editor.addOption')}
                </Button>
                <TextField
                  select
                  size="small"
                  label={t('exercises.editor.correctOption')}
                  value={gap.correct}
                  disabled={disabled}
                  onChange={(event) => updateGap(index, { ...gap, correct: Number(event.target.value) })}
                  sx={{ minWidth: 200 }}
                >
                  {gap.options.map((option, optionIndex) => (
                    <MenuItem key={optionIndex} value={optionIndex}>
                      {option || t('exercises.editor.optionNumber', { number: optionIndex + 1 })}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}

/** Общий редактор «собери предложение»: слова и принимаемые варианты сборки */
export function SentenceBuilderEditor({
  content,
  onChange,
  disabled,
}: EditorProps<{ words: string[]; accepted: string[] }>) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <CommaListField
        label={t('exercises.editor.words')}
        helperText={t('exercises.editor.wordsHint')}
        values={content.words}
        onChange={(words) => onChange({ ...content, words })}
        disabled={disabled}
      />
      <StringListEditor
        label={t('exercises.editor.acceptedSentences')}
        values={content.accepted}
        onChange={(accepted) => onChange({ ...content, accepted })}
        addLabel={t('exercises.editor.addSentence')}
        disabled={disabled}
      />
      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.sentenceBuilderScoring')}
      </Typography>
    </Stack>
  )
}
